/**
 * The docs teammate's guides carry a small, deliberately flat YAML front
 * matter block (`docs/guides/*.md`): five scalar keys, one of them a
 * single-line bracketed list (`sources`) that this reader never needs to
 * parse structurally. A hand-rolled line parser is enough for that shape and
 * avoids pulling in a YAML dependency for five key: value pairs.
 *
 * A guide missing front matter, or malformed, still renders (as "Untitled
 * guide") rather than taking the whole Guides section down -- the same
 * degrade-not-crash rule the rest of the portal already follows for a failed
 * fetch.
 */
export interface GuideMeta {
  title: string
  slug: string
  audience?: string
  updated?: string
}

export interface ParsedGuide extends GuideMeta {
  body: string
}

const FRONT_MATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/

export function parseFrontMatter(raw: string, fallbackSlug: string): ParsedGuide {
  const match = raw.match(FRONT_MATTER)
  if (!match) {
    return { title: fallbackSlug, slug: fallbackSlug, body: raw }
  }
  const front = match[1] ?? ''
  const body = match[2] ?? ''
  const meta: Record<string, string> = {}
  for (const line of front.split(/\r?\n/)) {
    const kv = line.match(/^([A-Za-z_]+):\s?(.*)$/)
    if (kv && kv[1] !== undefined && kv[2] !== undefined) {
      meta[kv[1]] = kv[2].trim()
    }
  }
  const result: ParsedGuide = {
    title: meta.title || fallbackSlug,
    slug: meta.slug || fallbackSlug,
    body,
  }
  if (meta.audience) result.audience = meta.audience
  if (meta.updated) result.updated = meta.updated
  return result
}
